import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import { formatWholeNumber } from '@UIComponents/utils/formatters';
import './DailyImpressionsChart.scss';

type DailyImpressionsChartProps = {
  readonly dailyImpressions: number[];
  readonly premiereDate: string;
  readonly period: string;
};

const formatDateLabel = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function DailyImpressionsChart({
  dailyImpressions,
  premiereDate,
  period,
}: DailyImpressionsChartProps) {
  const startDate = new Date(premiereDate);

  const data = dailyImpressions.map((impressions, index) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + index);
    return {
      day: index + 1,
      date: formatDateLabel(date),
      impressions,
    };
  });

  // Show fewer x-axis ticks for readability
  const tickInterval = Math.max(1, Math.floor(data.length / 10));

  // Milestone indices that get special treatment
  const milestoneIndices = new Set([0, 29, 59, 89]);

  // Find days with zero impressions to highlight them (excluding days that get special markers)
  const zeroDays = data.filter(
    (d, index) => !milestoneIndices.has(index) && d.impressions === 0,
  );

  // First day marker - blue if has data, red if no data
  const firstDay = data[0];
  const firstDayHasData = firstDay && firstDay.impressions > 0;

  // Milestone markers at day 30, 60, 90 (when showing longer periods)
  const milestones = [
    { day: 30, index: 29 },
    { day: 60, index: 59 },
    { day: 90, index: 89 },
    // Only show milestones if we have more data after them
  ].filter((m) => m.index < data.length - 1 && data[m.index]);

  return (
    <div className="daily-impressions-chart">
      <h4 className="viewership-detail__info__title">
        Daily Impressions - {period}
      </h4>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart
          data={data}
          margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            interval={tickInterval}
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            tickFormatter={(value) => formatWholeNumber(value)}
            tick={{ fontSize: 11 }}
            width={50}
          />
          <Tooltip
            formatter={(value: number) => [
              formatWholeNumber(value),
              'Impressions',
            ]}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Line
            type="monotone"
            dataKey="impressions"
            stroke="#4a90d9"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          {/* First day marker - blue if has data, red if no data */}
          {firstDay && (
            <ReferenceDot
              key="first-day"
              x={firstDay.date}
              y={firstDay.impressions}
              r={4}
              fill={firstDayHasData ? '#4a90d9' : '#e74c3c'}
              stroke={firstDayHasData ? '#3a7bc8' : '#c0392b'}
              strokeWidth={1}
            />
          )}
          {/* Milestone markers at day 30, 60, 90 - blue if has data, red if no data */}
          {milestones.map((milestone) => {
            const point = data[milestone.index];
            const hasData = point.impressions > 0;
            return (
              <ReferenceDot
                key={`milestone-${milestone.day}`}
                x={point.date}
                y={point.impressions}
                r={4}
                fill={hasData ? '#4a90d9' : '#e74c3c'}
                stroke={hasData ? '#3a7bc8' : '#c0392b'}
                strokeWidth={1}
              />
            );
          })}
          {/* Red dots for other days with zero impressions */}
          {zeroDays.map((point) => (
            <ReferenceDot
              key={point.date}
              x={point.date}
              y={0}
              r={2}
              fill="#e74c3c"
              stroke="#c0392b"
              strokeWidth={1}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default DailyImpressionsChart;
